import { FC, useMemo } from "react";

import {
  FilterByIdListAppliedFilterView,
  FilterByIdListItem,
} from "@/domain/view/components/datatable/datatable-filters/filter-by-id-list-applied-filter-view.tsx";

import { useSmartUserList } from "../../hooks/api/use-smart-user-list";
import { UserViewOptional } from "../user-view-optional";

import { DatatableAssigneeDisplayView } from "./datatable-assignee-display-view";

export interface DatatableAssigneeAppliedFilterViewProps {
  workspaceId: string;
  columnId: string;
  displayValue: string;
}

/**
 * Applied filter view for assignee column in datatables.
 *
 * **Security**: Uses `useSmartUserList` to fetch user data safely in both
 * public and private contexts:
 * - Public dashboards: Shows sanitized user data (no email/username)
 * - Private dashboards: Shows full user data
 */
export const DatatableAssigneeAppliedFilterView: FC<DatatableAssigneeAppliedFilterViewProps> = ({
  columnId,
  displayValue,
}) => {
  // SECURITY: Use smart hook that switches between public/private data sources
  const { filterUsers } = useSmartUserList();

  const activeUserList = filterUsers({
    workspaceMembership: {
      status: ["ACTIVE"],
    },
  });

  const itemList = useMemo(() => {
    return activeUserList.map((user) => ({
      id: user.id,
      name: user.username ?? user.email,
    }));
  }, [activeUserList]);

  const renderItem = (item: FilterByIdListItem | null, view: "icon" | "text" | "icon-text") => {
    const showText = view === "text" || view === "icon-text";

    const user = activeUserList.find((user) => user.id === item?.id);

    return <UserViewOptional key={item?.id} user={user} logoOnly={!showText} />;
  };

  const renderDisplayView = () => {
    return <DatatableAssigneeDisplayView displayValue={displayValue} />;
  };

  return (
    <FilterByIdListAppliedFilterView
      columnId={columnId}
      itemList={itemList}
      renderItem={renderItem}
      renderDisplayView={renderDisplayView}
    />
  );
};
